import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { useDispatch, useSelector } from 'react-redux';
import { getOptInConfig, setOptInConfig, setOptInConfigForAllStores } from '../../../api/optinApi';
import translate from "../../../config/translations/translate";
import { translations } from '../../../config/translations/Feedback/OptIn';
import { setLoading } from '../../../commons/components/application/miscActions';
import { setReviewLeaderboardOptInStatus } from '../../../commons/components/application/applicationActions';
import { useCompanyAvailableStores } from '../../../commons/utils/useCompanyAvalaibleStores';
import { clearCacheForUrl } from '../../../api/cacheManager';

import OptInSwitch from './OptInSwitch';
import ConfirmationModal from './ConfirmModal';
import './OptInConfigContainer.scss';

const OptInConfigContainer = ({ t, availableCompanies, selectedCompany }) => {
  const [storesConfig, setStoresConfig] = useState([]);
  const [modalStore, setModalStore] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const dispatch = useDispatch();
  const loading = useSelector(state => state.misc.loading);
  const companyStores = useCompanyAvailableStores();

  useEffect(() => {
    if (selectedCompany && selectedCompany.id) {
      dispatch(setLoading(true));
      getOptInConfig(selectedCompany.id)
        .then(response => {
          const stores = response.data.data || [];
          setStoresConfig(stores);
          
          // Actualizar el estado de Redux en la carga inicial
          // Si alguna tienda tiene el flag en false, el estado debe ser true (mostrar alerta)
          // Si todas las tiendas tienen el flag en true, el estado debe ser false (no mostrar alerta)
          const hasStoreWithOptinDisabled = stores.some(store => store.reviewLeaderboardOptInEnabled === false);
          dispatch(setReviewLeaderboardOptInStatus(!hasStoreWithOptinDisabled));
        })
        .catch((error) => {
          console.error('OptInConfigContainer - Error loading optin status:', error);
        })
        .finally(() => dispatch(setLoading(false)));
    }
  }, []); 

  const handleSwitchChange = (store) => {
    const currentConfig = storesConfig.find(s => s.id === store.id);
    const newStatus = !(currentConfig && currentConfig.reviewLeaderboardOptInEnabled != null ? currentConfig.reviewLeaderboardOptInEnabled : false);
    if (newStatus) {
      updateOptInConfig(store.id, true);
    } else {
      setModalStore(store);
      setIsModalOpen(true);
    }
  };

  const updateOptInConfig = (storeId, enabled) => {
    dispatch(setLoading(true));
    setOptInConfig(storeId, enabled)
      .then(() => {
        // Limpiar el cache para asegurar que la próxima vez se obtengan datos frescos
        clearCacheForUrl('/review-leaderboard-optin');
        
        setStoresConfig(prev => {
          const updatedStores = prev.map(store => 
            store.id === storeId 
              ? { ...store, reviewLeaderboardOptInEnabled: enabled }
              : store
          );
          
          // Actualizar el estado de Redux
          // Si alguna tienda tiene el flag en false, el estado debe ser true (mostrar alerta)
          // Si todas las tiendas tienen el flag en true, el estado debe ser false (no mostrar alerta)
          const hasStoreWithOptinDisabled = updatedStores.some(store => store.reviewLeaderboardOptInEnabled === false);
          dispatch(setReviewLeaderboardOptInStatus(!hasStoreWithOptinDisabled));
          
          return updatedStores;
        });
      })
      .catch((error) => {
        console.error('OptInConfigContainer - API call failed:', error);
      })
      .finally(() => dispatch(setLoading(false)));
  };

  const handleConfirmDeactivation = () => {
    if (modalStore) {
      updateOptInConfig(modalStore.id, false);
    }
    setIsModalOpen(false);
    setModalStore(null);
  };

  const handleCancelDeactivation = () => {
    setIsModalOpen(false);
    setModalStore(null);
  };

  const handleEnableAllStores = () => {
    if (!selectedCompany || !selectedCompany.id) return;
    
    dispatch(setLoading(true));
    setOptInConfigForAllStores(selectedCompany.id, true)
      .then(() => {
        // Limpiar el cache
        clearCacheForUrl('/review-leaderboard-optin');
        
        // Actualizar todas las stores con el nuevo estado
        setStoresConfig(prev => 
          prev.map(store => ({ ...store, reviewLeaderboardOptInEnabled: true }))
        );
        
        // Actualizar el estado de Redux - todas activas = no mostrar alerta
        dispatch(setReviewLeaderboardOptInStatus(true));
      })
      .catch((error) => {
        console.error('OptInConfigContainer - Error enabling all stores:', error);
      })
      .finally(() => dispatch(setLoading(false)));
  };

  const handleDisableAllStores = () => {
    if (!selectedCompany || !selectedCompany.id) return;
    
    dispatch(setLoading(true));
    setOptInConfigForAllStores(selectedCompany.id, false)
      .then(() => {
        // Limpiar el cache
        clearCacheForUrl('/review-leaderboard-optin');
        
        // Actualizar todas las stores con el nuevo estado
        setStoresConfig(prev => 
          prev.map(store => ({ ...store, reviewLeaderboardOptInEnabled: false }))
        );
        
        // Actualizar el estado de Redux - alguna desactivada = mostrar alerta
        dispatch(setReviewLeaderboardOptInStatus(false));
      })
      .catch((error) => {
        console.error('OptInConfigContainer - Error disabling all stores:', error);
      })
      .finally(() => dispatch(setLoading(false)));
  };

  // Merge company stores with config data
  const storesWithConfig = (companyStores || []).map(store => {
    const config = storesConfig.find(s => s.id === store.id);
    return {
      ...store,
      reviewLeaderboardOptInEnabled: config && config.reviewLeaderboardOptInEnabled != null ? config.reviewLeaderboardOptInEnabled : false
    };
  });

  console.log("storesWithConfig", storesWithConfig);

  return (
    <div className="optin-config-center-container">
      <h1 style={styles.title}>{t('optin.title')}</h1>
      <p style={styles.description}>
        {t('optin.description')}
      </p>

      <div style={styles.bulkActionsContainer}>
        <button 
          style={styles.bulkActionButton}
          onClick={handleEnableAllStores}
          disabled={loading}
        >
          ✓ {t('optin.enableAllStores')}
        </button>
        <button 
          style={{...styles.bulkActionButton, ...styles.bulkActionButtonDisable}}
          onClick={handleDisableAllStores}
          disabled={loading}
        >
          ✕ {t('optin.disableAllStores')}
        </button>
      </div>

      <div style={styles.switchContainer}>
        {storesWithConfig.map((store) => {
          const currentStore = store; // Captura explícita del store actual
          return (
            <div key={currentStore.id} style={styles.storeSwitchItem}>
              <div style={styles.storeName}>{currentStore.name}</div>
              <OptInSwitch
                id={`opt-in-switch-${currentStore.id}`}
                label={t('optin.optInLabel')}
                checked={currentStore.reviewLeaderboardOptInEnabled}
                onChange={() => handleSwitchChange(currentStore)}
                disabled={loading}
              />
            </div>
          );
        })}
      </div>

      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={handleCancelDeactivation}
        onConfirm={handleConfirmDeactivation}
        t={t}
      />
    </div>
  );
};

const styles = {
  container: {
    backgroundColor: 'var(--surface)',
    width: '100%',
    maxWidth: 300,
    padding: '2.5rem',
    borderRadius: '12px',
    boxShadow: 'var(--shadow-lg)',
    textAlign: 'center',
  },
  title: {
    fontSize: '1.75rem',
    fontWeight: 600,
    color: 'var(--text-primary)',
    marginBottom: '0.5rem',
  },
  description: {
    fontSize: '1rem',
    color: 'var(--text-secondary)',
    marginBottom: '2.5rem',
  },
  bulkActionsContainer: {
    display: 'flex',
    gap: '1rem',
    marginBottom: '1.5rem',
    justifyContent: 'flex-start',
  },
  bulkActionButton: {
    padding: '0.75rem 1.5rem',
    fontSize: '0.95rem',
    fontWeight: 500,
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    backgroundColor: '#4CAF50',
    color: 'white',
    transition: 'all 0.2s ease',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  },
  bulkActionButtonDisable: {
    backgroundColor: '#f44336',
  },
  switchContainer: {
    maxWidth: 800,
    padding: '1.5rem',
    border: '1px solid var(--border)',
    borderRadius: '8px',
    backgroundColor: '#fdfdff',
  },
  storeSwitchItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1rem',
    marginBottom: '0.5rem',
    borderBottom: '1px solid var(--border)',
  },
  storeName: {
    fontSize: '1rem',
    fontWeight: 500,
    color: 'var(--text-primary)',
    flex: 1,
  },
};

const mapStateToProps = (state) => ({
  availableCompanies: state.application.availableCompanies,
  selectedCompany: state.application.selectedCompany,
});

OptInConfigContainer.translations = {
  ...translations,
};

export default connect(mapStateToProps)(translate(OptInConfigContainer));
